/**
 * Curated content-language picker for courses.
 *
 * This is a UI convenience, NOT a validator: `languageCodeSchema`
 * (`zod/courses.ts`) still accepts any lowercase BCP-47-ish code, so a tenant
 * teaching in something we never listed is never blocked — the label helper
 * just falls back to the raw code. There is no i18n framework in this repo
 * (roadmap Phase 8); `courses.language` describes the CONTENT, it does not
 * translate the UI.
 *
 * Ordered Philippines-first because that is where the tenants are.
 */
export const COURSE_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'fil', label: 'Filipino' },
  { code: 'ceb', label: 'Cebuano' },
  { code: 'ilo', label: 'Ilocano' },
  { code: 'hil', label: 'Hiligaynon' },
  { code: 'zh', label: 'Chinese' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
  { code: 'es', label: 'Spanish' },
  { code: 'ar', label: 'Arabic' },
  { code: 'id', label: 'Indonesian' },
  { code: 'ms', label: 'Malay' },
  { code: 'th', label: 'Thai' },
  { code: 'vi', label: 'Vietnamese' },
] as const;

export type CourseLanguageCode = (typeof COURSE_LANGUAGES)[number]['code'];

/**
 * Display label for a stored language code. Unknown / uncurated codes render
 * upper-cased ('zh-hant' → 'ZH-HANT') rather than disappearing, so a course
 * authored outside the picker still shows its language.
 */
export function courseLanguageLabel(code: string | null | undefined): string | null {
  if (!code) return null;
  const hit = COURSE_LANGUAGES.find((l) => l.code === code.toLowerCase());
  return hit ? hit.label : code.toUpperCase();
}
